import { useEffect, useState } from "react";
import { CalendarClock, CheckCircle2, ClipboardList, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fmtDue = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "no due date");
const statusClass = { done: "bg-emerald-50 text-emerald-700", overdue: "bg-rose-50 text-rose-700", assigned: "bg-indigo-50 text-indigo-700" };

export const ClientAssignments = ({ client, tutorials }) => {
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(false);
  const [tutorialId, setTutorialId] = useState("");
  const [due, setDue] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/assignments/client/${client.id}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, [client.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const assign = async (e) => {
    e.preventDefault();
    if (!tutorialId) { toast.error("Choose a tutorial"); return; }
    setSaving(true);
    try {
      await api.post("/assignments", { client_id: client.id, tutorial_id: tutorialId, due_date: due || null, note });
      toast.success("Assigned");
      setAdding(false); setTutorialId(""); setDue(""); setNote("");
      load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setSaving(false); }
  };
  const remove = async (a) => {
    try { await api.delete(`/assignments/${a.id}`); toast.success("Unassigned"); load(); } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <div className="w-full mt-3 rounded-2xl bg-slate-50 border border-slate-100 p-4" data-testid={`assignments-${client.id}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1"><ClipboardList className="w-3.5 h-3.5" /> Assigned exercises {items ? `(${items.length})` : ""}</p>
        <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => setAdding((v) => !v)} disabled={client.status === "invited"} data-testid={`assign-toggle-${client.id}`}>{adding ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />} {adding ? "Cancel" : "Assign"}</Button>
      </div>
      {adding && (
        <form onSubmit={assign} className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_160px_1fr_auto] gap-2" data-testid={`assign-form-${client.id}`}>
          <Select value={tutorialId} onValueChange={setTutorialId}>
            <SelectTrigger className="bg-white rounded-xl h-10" data-testid={`assign-tutorial-select-${client.id}`}><SelectValue placeholder="Choose a tutorial" /></SelectTrigger>
            <SelectContent className="max-h-72">{tutorials.filter((t) => t.audience !== "clinicians").map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="bg-white rounded-xl h-10" data-testid={`assign-due-${client.id}`} />
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for the client (optional)" className="bg-white rounded-xl h-10" data-testid={`assign-note-${client.id}`} />
          <Button type="submit" disabled={saving} className="rounded-xl h-10 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={`assign-submit-${client.id}`}>Assign</Button>
        </form>
      )}
      {items && items.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid={`assignments-list-${client.id}`}>
          {items.map((a) => (
            <li key={a.id} className="flex items-center gap-3 text-sm bg-white rounded-xl px-3 py-2 border border-slate-100" data-testid={`assignment-${a.id}`}>
              {a.status === "done" ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> : <CalendarClock className="w-4 h-4 text-slate-400 shrink-0" />}
              <span className="flex-1 min-w-0 truncate font-medium text-slate-900">{a.tutorial.title}</span>
              <span className="text-xs text-slate-500 hidden sm:inline">{fmtDue(a.due_date)}</span>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${statusClass[a.status]}`} data-testid={`assignment-status-${a.id}`}>{a.status}</span>
              <button type="button" onClick={() => remove(a)} className="text-slate-400 hover:text-rose-600" aria-label="Unassign" data-testid={`unassign-${a.id}`}><X className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      )}
      {client.status === "invited" && <p className="text-xs text-slate-500 mt-2">You can assign exercises once the client has accepted the invitation.</p>}
    </div>
  );
};
